import { DateFilterSelector } from '../../common/DateFilterSelector';
import { useLanguage } from '../../../contexts/LanguageContext';
import type { CommunicationChannel } from '../../../types';
import { defaultChannels } from '../../../config/channels';
import { channelIcons } from '../../../utils/Constants';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../../ui/sheet';

interface MobileFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableChannels: CommunicationChannel[];
  selectedChannelIds: Set<string>;
  onToggleChannel: (channelId: string) => void;
  showExternal: boolean;
  showInternal: boolean;
  onToggleExternal: () => void;
  onToggleInternal: () => void;
  // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — miroir mobile du filtre « mes posts ».
  // Optionnels : ce modal est aussi monté ailleurs que dans la galerie de posts.
  showOnlyMine?: boolean;
  onToggleOnlyMine?: () => void;
  canFilterMine?: boolean;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
}

export function MobileFiltersModal({
  isOpen,
  onClose,
  availableChannels,
  selectedChannelIds,
  onToggleChannel,
  showExternal,
  showInternal,
  onToggleExternal,
  onToggleInternal,
  showOnlyMine = false,
  onToggleOnlyMine,
  canFilterMine = false,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: MobileFiltersModalProps) {
  const { t } = useLanguage();

  // Safety check: return null if translations are not loaded
  if (!t || !t.posts) return null;

  const getChannelIcon = (channel: CommunicationChannel): string => {
    return channelIcons[channel.network] || (channel.network || '?').charAt(0);
  };

  const getChannelLabel = (channel: CommunicationChannel): string => {
    if (channel.customName) {
      return channel.customName;
    }
    const config = defaultChannels.find(c => c.name === channel.network);
    return (config?.translationKey && t.networks ? t.networks[config.translationKey as keyof typeof t.networks] : config?.displayName) || channel.network;
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="bottom"
        className="h-full w-full max-w-full p-0 overflow-auto"
        style={{ backgroundColor: 'var(--color-background)' }}
      >
        <SheetTitle className="sr-only">{t.posts.filters}</SheetTitle>
        <SheetDescription className="sr-only">{t.posts.filters}</SheetDescription>

        {/* Header */}
        <div
          className="sticky top-0 z-10 px-4 py-4 flex items-center justify-between"
          style={{
            backgroundColor: 'var(--color-background)',
            borderBottom: `1px solid var(--color-border)`,
          }}
        >
          <h2
            className="font-semibold text-lg"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t.posts.filters}
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-surface)' }}
          >
            <span style={{ color: 'var(--color-text-primary)' }}>✕</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Internal/External filter */}
          <div>
            <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
              {t.common.type}
            </h3>
            <div className="flex items-center gap-4">
              <button
                onClick={onToggleExternal}
                className="flex items-center gap-2"
              >
                <div
                  className={`w-4 h-4 rounded-full transition-all ${
                    showExternal ? '' : 'border bg-transparent'
                  }`}
                  style={showExternal ? {
                    backgroundColor: 'var(--color-primary)',
                    boxShadow: '0 0 8px rgba(85, 0, 255, 0.6)'
                  } : {
                    borderColor: 'var(--color-accent)'
                  }}
                />
                <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  {t.posts.external}
                </span>
              </button>
              <button
                onClick={onToggleInternal}
                className="flex items-center gap-2"
              >
                <div
                  className={`w-4 h-4 rounded-full transition-all ${
                    showInternal ? '' : 'border bg-transparent'
                  }`}
                  style={showInternal ? {
                    backgroundColor: 'var(--color-primary)',
                    boxShadow: '0 0 8px rgba(85, 0, 255, 0.6)'
                  } : {
                    borderColor: 'var(--color-accent)'
                  }}
                />
                <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  {t.posts.internal}
                </span>
              </button>
              {/* V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — « mes posts » (miroir mobile) */}
              {canFilterMine && onToggleOnlyMine && (
                <button
                  onClick={onToggleOnlyMine}
                  className="flex items-center gap-2"
                  aria-pressed={showOnlyMine}
                >
                  <div
                    className={`w-4 h-4 rounded-full transition-all ${
                      showOnlyMine ? '' : 'border bg-transparent'
                    }`}
                    style={showOnlyMine ? {
                      backgroundColor: 'var(--color-primary)',
                      boxShadow: '0 0 8px rgba(85, 0, 255, 0.6)'
                    } : {
                      borderColor: 'var(--color-accent)'
                    }}
                  />
                  <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    {t.posts.onlyMine}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Channels */}
          <div>
            <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
              {t.channels.title}
            </h3>
            <div className="space-y-2">
              {availableChannels.map((channel) => {
                const networkConfig = defaultChannels.find(c => c.name === channel.network);
                const isActive = selectedChannelIds.has(channel.id);

                return (
                  <button
                    key={channel.id}
                    onClick={() => onToggleChannel(channel.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors"
                    style={{
                      backgroundColor: isActive ? 'var(--color-surface)' : 'transparent',
                      border: `1px solid ${isActive ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
                      style={{
                        backgroundColor: networkConfig?.color || 'var(--color-surface-elevated)',
                        color: networkConfig?.textColor || 'var(--color-text-primary)'
                      }}
                    >
                      {getChannelIcon(channel)}
                    </div>
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {getChannelLabel(channel)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date filters */}
          <div>
            <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
              {t.posts.filterByDate || 'Date'}
            </h3>
            <DateFilterSelector
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={onStartDateChange}
              onEndDateChange={onEndDateChange}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
